import { Box, Checkbox, Chip, FormControlLabel, FormHelperText, MenuItem, TextField } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import type { Optie } from '../helpers/labels.ts';

interface BasisProps {
  naam: string;
  label: string;
  verplicht?: boolean | undefined;
  hulptekst?: string | undefined;
  uitgeschakeld?: boolean | undefined;
  autoFocus?: boolean | undefined;
}

const verplichtRegel = { required: 'Dit veld is verplicht' };

/**
 * Een tekstveld in een formulier. De waarde is altijd een tekst; een leeg veld wordt door de API als leeg gelezen.
 * @param props De naam in het formulier, het label en de soort invoer.
 * @returns Het veld.
 */
export function Tekstveld({
  naam,
  label,
  verplicht,
  hulptekst,
  uitgeschakeld,
  autoFocus,
  type = 'text',
  meerregelig,
  rijen,
  placeholder,
  stap,
  min,
  max,
}: BasisProps & {
  type?: 'text' | 'email' | 'tel' | 'url' | 'number' | 'date' | 'datetime-local' | undefined;
  meerregelig?: boolean | undefined;
  rijen?: number | undefined;
  placeholder?: string | undefined;
  stap?: number | string | undefined;
  min?: number | string | undefined;
  max?: number | string | undefined;
}) {
  const { control } = useFormContext();
  const vasteLabel = type === 'date' || type === 'datetime-local';
  return (
    <Controller
      name={naam}
      control={control}
      {...(verplicht ? { rules: verplichtRegel } : {})}
      render={({ field, fieldState }) => (
        <TextField
          {...field}
          value={field.value ?? ''}
          label={label}
          type={type}
          required={verplicht ?? false}
          disabled={uitgeschakeld ?? false}
          autoFocus={autoFocus ?? false}
          error={Boolean(fieldState.error)}
          helperText={fieldState.error?.message ?? hulptekst}
          multiline={meerregelig ?? false}
          minRows={rijen}
          placeholder={placeholder}
          fullWidth
          size="small"
          slotProps={{
            ...(vasteLabel ? { inputLabel: { shrink: true } } : {}),
            htmlInput: {
              ...(stap !== undefined ? { step: stap } : {}),
              ...(min !== undefined ? { min } : {}),
              ...(max !== undefined ? { max } : {}),
              ...(type === 'number' ? { inputMode: 'decimal' } : {}),
            },
          }}
        />
      )}
    />
  );
}

/**
 * Een keuzelijst met één keuze. Een lege keuze is mogelijk tenzij het veld verplicht is.
 * @param props De naam, het label en de opties.
 * @returns Het veld.
 */
export function Keuzeveld<T extends string>({
  naam,
  label,
  opties,
  verplicht,
  hulptekst,
  uitgeschakeld,
  leegLabel = 'Geen',
}: BasisProps & { opties: readonly Optie<T>[]; leegLabel?: string | undefined }) {
  const { control } = useFormContext();
  return (
    <Controller
      name={naam}
      control={control}
      {...(verplicht ? { rules: verplichtRegel } : {})}
      render={({ field, fieldState }) => (
        <TextField
          {...field}
          value={field.value ?? ''}
          select
          label={label}
          required={verplicht ?? false}
          disabled={uitgeschakeld ?? false}
          error={Boolean(fieldState.error)}
          helperText={fieldState.error?.message ?? hulptekst}
          fullWidth
          size="small"
        >
          <MenuItem value="" disabled={verplicht ?? false}>
            <em>{verplicht ? 'Maak een keuze' : leegLabel}</em>
          </MenuItem>
          {opties.map((optie) => (
            <MenuItem key={optie.waarde} value={optie.waarde}>
              {optie.label}
            </MenuItem>
          ))}
        </TextField>
      )}
    />
  );
}

/**
 * Een keuzelijst met meerdere keuzes, getoond als chips.
 * @param props De naam, het label en de opties.
 * @returns Het veld.
 */
export function Meerkeuzeveld<T extends string>({
  naam,
  label,
  opties,
  hulptekst,
  uitgeschakeld,
}: BasisProps & { opties: readonly Optie<T>[] }) {
  const { control } = useFormContext();
  const labelVan = (waarde: string) => opties.find((optie) => optie.waarde === waarde)?.label ?? waarde;
  return (
    <Controller
      name={naam}
      control={control}
      render={({ field, fieldState }) => (
        <TextField
          {...field}
          value={(field.value as string[] | undefined) ?? []}
          select
          label={label}
          disabled={uitgeschakeld ?? false}
          error={Boolean(fieldState.error)}
          helperText={fieldState.error?.message ?? hulptekst}
          fullWidth
          size="small"
          slotProps={{
            select: {
              multiple: true,
              renderValue: (geselecteerd) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(geselecteerd as string[]).map((waarde) => (
                    <Chip key={waarde} label={labelVan(waarde)} size="small" />
                  ))}
                </Box>
              ),
            },
          }}
        >
          {opties.map((optie) => (
            <MenuItem key={optie.waarde} value={optie.waarde}>
              {optie.label}
            </MenuItem>
          ))}
        </TextField>
      )}
    />
  );
}

/**
 * Een aan-uitvinkje.
 * @param props De naam en het label.
 * @returns Het veld.
 */
export function Vinkveld({ naam, label, hulptekst, uitgeschakeld }: BasisProps) {
  const { control } = useFormContext();
  return (
    <Controller
      name={naam}
      control={control}
      render={({ field, fieldState }) => (
        <Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(field.value)}
                onChange={(gebeurtenis) => field.onChange(gebeurtenis.target.checked)}
                onBlur={field.onBlur}
                name={field.name}
                disabled={uitgeschakeld ?? false}
              />
            }
            label={label}
          />
          {(fieldState.error?.message ?? hulptekst) && (
            <FormHelperText error={Boolean(fieldState.error)}>{fieldState.error?.message ?? hulptekst}</FormHelperText>
          )}
        </Box>
      )}
    />
  );
}
